import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import { DataPagination } from '../components/DataPagination';
import {
  CreditCard,
  MagnifyingGlass,
  ArrowsClockwise,
  IdentificationCard,
  CheckCircle,
  XCircle,
  User,
  Spinner,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function CardCentrePage() {
  const { isSuperAdmin } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterOccupied, setFilterOccupied] = useState('all');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cardDetail, setCardDetail] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const fetchCards = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/cards`;
      if (filterEntity && filterEntity !== 'all') url += `?entity_id=${filterEntity}`;
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCards(data);
    } catch (error) {
      console.error('Error fetching cards:', error);
      toast.error('Failed to fetch cards');
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  const syncFromMiddleware = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const entityParam = filterEntity && filterEntity !== 'all' ? `?entity_id=${filterEntity}` : '';
      await axios.get(`${API_URL}/api/cards/sync-middleware${entityParam}`, { withCredentials: true, headers });
      await fetchCards();
    } catch (e) { /* silent */ }
  }, [filterEntity, fetchCards]);

  useEffect(() => { fetchCards(); syncFromMiddleware(); }, [fetchCards, syncFromMiddleware]);

  const openCardDetails = async (card) => {
    setCardDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/cards/${card.id}/details`, {
        withCredentials: true, headers
      });
      setCardDetail(data);
    } catch (error) {
      const msg = formatApiError(error, 'Failed to fetch card details');
      toast.error(msg);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredCards = cards.filter(card => {
    const matchesSearch =
      (card.card_no || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (card.card_seq || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (card.batch_no || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOccupied = filterOccupied === 'all' || card.occupied === filterOccupied;
    return matchesSearch && matchesOccupied;
  });

  const total = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedCards = filteredCards.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [searchQuery, filterEntity, filterOccupied, pageSize]);

  return (
    <div className="space-y-3 pb-6" data-testid="card-centre-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Card Centre
        </h1>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-xl p-3">
          <p className="text-[10px] text-[#78716C] uppercase tracking-wide" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Total</p>
          <p className="text-xl font-semibold text-[#1C1917] mt-0.5 font-mono" data-testid="total-cards-count">{cards.length.toLocaleString()}</p>
        </div>
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-xl p-3">
          <p className="text-[10px] text-[#78716C] uppercase tracking-wide" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Active</p>
          <p className="text-xl font-semibold text-emerald-700 mt-0.5 font-mono">{cards.filter(c => c.status === 'Active').length.toLocaleString()}</p>
        </div>
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-xl p-3">
          <p className="text-[10px] text-[#78716C] uppercase tracking-wide" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Occupied</p>
          <p className="text-xl font-semibold text-[#0A2540] mt-0.5 font-mono">{cards.filter(c => c.occupied === 'Y').length.toLocaleString()}</p>
        </div>
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-xl p-3">
          <p className="text-[10px] text-[#78716C] uppercase tracking-wide" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Available</p>
          <p className="text-xl font-semibold text-[#78716C] mt-0.5 font-mono">{cards.filter(c => c.occupied === 'N').length.toLocaleString()}</p>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by card no, sequence or batch…"
        testId="cards-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); setFilterOccupied('all'); }}
        filters={[
          ...(isSuperAdmin ? [{
            key: 'entity',
            label: 'Entity',
            value: filterEntity,
            onChange: setFilterEntity,
            options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
          }] : []),
          {
            key: 'occupied',
            label: 'Occupied',
            value: filterOccupied,
            onChange: setFilterOccupied,
            options: [
              { value: 'all', label: 'All cards' },
              { value: 'Y', label: 'Occupied' },
              { value: 'N', label: 'Available' },
            ],
          },
        ]}
      />

      <DataTable
        columns={[
          { key: 'card_no', label: 'Card No', width: 'minmax(180px,1.4fr)', render: (c) => (
            <span className="flex items-center gap-3 min-w-0">
              <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center group-hover:bg-[#EDE6D8] ${c.occupied === 'Y' ? 'bg-[#0A2540]/15' : 'bg-slate-200/60'}`}>
                <CreditCard size={14} className={`group-hover:text-[#1C1917] ${c.occupied === 'Y' ? 'text-[#0A2540]' : 'text-slate-500'}`} />
              </span>
              <span className="font-mono text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917] truncate">
                {c.card_no}
              </span>
            </span>
          ) },
          { key: 'card_seq', label: 'Seq', width: 'minmax(80px,0.7fr)', mono: true, tone: 'muted' },
          ...(isSuperAdmin ? [{ key: 'entity_name', label: 'Entity', width: 'minmax(140px,1.2fr)', tone: 'muted' }] : []),
          { key: 'batch_no', label: 'Batch', width: 'minmax(100px,0.9fr)', mono: true, tone: 'muted' },
          { key: 'status', label: 'Status', width: 'minmax(100px,0.9fr)', render: (c) => (
            <span className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'Active' ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
              <span className={`text-xs font-medium ${c.status === 'Active' ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {c.status}
              </span>
            </span>
          ) },
          { key: 'occupied', label: 'Occupied', width: 'minmax(100px,0.9fr)', render: (c) => c.occupied === 'Y' ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#0A2540] group-hover:text-[#1C1917]">
              <CheckCircle size={12} weight="fill" /> Yes
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#78716C] group-hover:text-[#78716C]">
              <XCircle size={12} /> No
            </span>
          ) },
          { key: 'virtual_card', label: 'Virtual', width: 'minmax(80px,0.7fr)', mono: true, tone: 'muted',
            render: (c) => c.virtual_card === 'Y' ? 'Yes' : 'No' },
          { key: 'date_registered', label: 'Registered', width: 'minmax(110px,1fr)', mono: true, tone: 'muted',
            render: (c) => c.date_registered ? c.date_registered.split(' ')[0] : '—' },
        ]}
        rows={pagedCards}
        loading={loading}
        rowKey={(c) => c.id}
        onRowClick={(c) => openCardDetails(c)}
        emptyIcon={<IdentificationCard size={20} className="text-slate-500" />}
        emptyTitle="No cards yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Sync from middleware to populate cards.'}
        testIdPrefix="card"
        actionRender={(c) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); openCardDetails(c); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-card-${c.id}`}
              aria-label="View card"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      <DataPagination
        page={safePage}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        testId="cards-pagination"
      />

      {/* Card Details Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Card Details
            </DialogTitle>
            <DialogDescription className="sr-only">
              Detailed information about the selected card from middleware
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="card-detail-loading">
              <Spinner size={32} className="animate-spin text-[#0A2540]" />
            </div>
          ) : cardDetail ? (
            <div className="space-y-4" data-testid="card-detail-content">
              {/* Card number header */}
              <div className="flex items-center gap-3 pb-3 border-b border-[#EDE6D8]">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-white ${
                  cardDetail.occupied === 'Y' ? 'bg-[#0A2540]' : 'bg-[#78716C]'
                }`}>
                  <CreditCard size={22} />
                </div>
                <div>
                  <p className="font-semibold text-lg text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="card-detail-card-no">
                    {cardDetail.card_no}
                  </p>
                  {isSuperAdmin && (
                    <p className="text-xs text-[#78716C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {cardDetail.entity_name || 'Unknown Entity'}
                    </p>
                  )}
                </div>
              </div>

              {/* Subscriber */}
              {cardDetail.sub_name && (
                <div className="flex items-center gap-3 p-3 bg-[#0A2540]/5 rounded-lg" data-testid="card-detail-subscriber">
                  <User size={18} className="text-[#0A2540]" />
                  <div>
                    <p className="text-xs text-[#78716C] uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Subscriber</p>
                    <p className="font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{cardDetail.sub_name}</p>
                  </div>
                </div>
              )}

              {/* Detail grid */}
              <div className="grid grid-cols-2 gap-3">
                <DetailField label="Card LUHN" value={cardDetail.card_luhn} testId="card-detail-luhn" />
                <DetailField label="Sequence" value={cardDetail.card_seq} testId="card-detail-seq" />
                <DetailField label="Status" value={cardDetail.status} testId="card-detail-status" badge={cardDetail.status === 'Active' ? 'green' : 'red'} />
                <DetailField label="Occupied" value={cardDetail.occupied === 'Y' ? 'Yes' : 'No'} testId="card-detail-occupied" badge={cardDetail.occupied === 'Y' ? 'blue' : 'gray'} />
                <DetailField label="Virtual Card" value={cardDetail.virtual_card === 'Y' ? 'Yes' : 'No'} testId="card-detail-virtual" />
                <DetailField label="Batch No" value={cardDetail.batch_no} testId="card-detail-batch" />
                <DetailField label="UID Hex" value={cardDetail.uid_hex || '-'} testId="card-detail-uid-hex" />
                <DetailField label="UID Dec" value={cardDetail.uid_dec || '-'} testId="card-detail-uid-dec" />
                <DetailField label="Maker" value={cardDetail.maker_id} testId="card-detail-maker" />
                <DetailField label="MW Entity ID" value={cardDetail.middleware_entity_id} testId="card-detail-mw-entity" />
                <DetailField label="Registered" value={cardDetail.date_registered} testId="card-detail-registered" />
                <DetailField label="Last Updated" value={cardDetail.date_updated} testId="card-detail-updated" />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ label, value, testId, badge }) {
  const badgeColors = {
    green: 'bg-[#10B981]/10 text-[#10B981]',
    red: 'bg-[#EF4444]/10 text-[#EF4444]',
    blue: 'bg-[#0A2540]/10 text-[#0A2540]',
    gray: 'bg-[#FCFAF6] text-[#78716C]',
  };

  return (
    <div data-testid={testId}>
      <p className="text-[10px] text-[#78716C] uppercase mb-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      {badge ? (
        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${badgeColors[badge]}`}>
          {value || '-'}
        </span>
      ) : (
        <p className="text-sm font-medium text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value || '-'}</p>
      )}
    </div>
  );
}
